/**
 * Version Check Utility
 * Checks for application updates by comparing version meta tags
 */

export class VersionChecker {
  constructor(options = {}) {
    this.checkInterval = options.checkInterval || 5 * 60 * 1000; // Default: 5 minutes
    this.onUpdateAvailable = options.onUpdateAvailable || (() => {});
    this.intervalId = null;
    this.currentVersion = this.getCurrentVersion();
  }

  /**
   * Get the current version from meta tag
   */
  getCurrentVersion() {
    const metaTag = document.querySelector('meta[name="app-version"]');
    return metaTag ? metaTag.getAttribute('content') : null;
  }

  /**
   * Fetch the latest version from the server
   */
  async fetchLatestVersion() {
    try {
      // Add cache-busting parameter
      const response = await fetch(`/index.html?_=${Date.now()}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch version');
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const metaTag = doc.querySelector('meta[name="app-version"]');
      
      return metaTag ? metaTag.getAttribute('content') : null;
    } catch (error) {
      console.error('Error fetching latest version:', error);
      return null;
    }
  }

  /**
   * Check if an update is available
   */
  async checkForUpdate() {
    const latestVersion = await this.fetchLatestVersion();
    
    if (!latestVersion || !this.currentVersion) {
      return false;
    }

    if (latestVersion !== this.currentVersion) {
      console.log(`Update available: ${this.currentVersion} -> ${latestVersion}`);
      this.onUpdateAvailable(latestVersion);
      return true;
    }

    return false;
  }

  /**
   * Start periodic version checking
   */
  startPeriodicCheck() {
    // Initial check after 30 seconds
    setTimeout(() => {
      this.checkForUpdate();
    }, 30000);

    // Periodic checks
    this.intervalId = setInterval(() => {
      this.checkForUpdate();
    }, this.checkInterval);

    console.log(`Version checker started. Checking every ${this.checkInterval / 1000} seconds.`);
  }

  /**
   * Stop periodic version checking
   */
  stopPeriodicCheck() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('Version checker stopped.');
    }
  }

  /**
   * Force reload the application
   */
  static forceReload() {
    // Clear all caches before reload
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          caches.delete(cacheName);
        });
      });
    }

    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.unregister();
        });
      });
    }

    // Hard reload
    window.location.reload(true);
  }
}

export default VersionChecker;
